import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Linkedin,
  Download,
  Copy,
  Check,
  Share2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Shareable per-role social card.
 * - Renders the server-generated PNG inline (1200×630, /card.png endpoint)
 * - "Download image" — saves the PNG to disk
 * - "Copy post text" — pre-composed LinkedIn-friendly caption with the apply link
 * - "Share on LinkedIn" — opens LinkedIn's share dialog with the apply URL
 */
export default function SocialShareCard({
  jobTitle,
  jobDescription,
  cardImageUrl,
  publicApplyUrl,
  shareUrl,
  companyLabel,
}) {
  const [copied, setCopied] = useState(false);

  // shareUrl (OG-meta page) is preferred for posts so LinkedIn / Slack
  // auto-render the preview. Falls back to publicApplyUrl.
  const linkUrl = shareUrl || publicApplyUrl;

  const postText =
    `🟦 We're hiring — ${jobTitle}\n\n` +
    (jobDescription
      ? `${jobDescription.slice(0, 220)}${jobDescription.length > 220 ? "…" : ""}\n\n`
      : "") +
    `Apply directly (your résumé gets AI-scored in seconds): ${linkUrl}\n\n` +
    `#hiring #${(companyLabel || "team").replace(/[^a-zA-Z0-9]/g, "")} #${jobTitle.replace(/[^a-zA-Z0-9]/g, "")}`;

  const copyPost = async () => {
    try {
      await navigator.clipboard.writeText(postText);
      setCopied(true);
      toast.success("Post text copied — paste into LinkedIn");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  const downloadImage = async () => {
    try {
      const res = await fetch(cardImageUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hireloop-${jobTitle.replace(/[^a-zA-Z0-9-]+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Image downloaded");
    } catch {
      toast.error("Could not download image");
    }
  };

  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(linkUrl)}`;

  return (
    <section
      data-testid="social-share-card"
      className="border border-slate-200 bg-white"
    >
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
          <Share2 className="h-3 w-3" /> Share on LinkedIn
        </div>
        <a
          href={cardImageUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="social-share-image-link"
          className="text-xs font-medium text-blue-700 hover:underline"
        >
          Open PNG
        </a>
      </div>
      <div className="grid gap-px bg-slate-100 md:grid-cols-2">
        {/* Image preview */}
        <div className="bg-white p-5">
          <div className="border border-slate-200 bg-slate-50 p-2">
            <img
              src={cardImageUrl}
              alt={`Share card for ${jobTitle}`}
              data-testid="social-share-image-preview"
              loading="lazy"
              className="block h-auto w-full"
            />
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            1200 × 630 PNG — optimised for LinkedIn, X, and Open Graph previews.
          </p>
        </div>

        {/* Actions + caption */}
        <div className="space-y-4 bg-white p-5">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
              Pre-composed post
            </div>
            <textarea
              data-testid="social-share-post-text"
              readOnly
              value={postText}
              onClick={(e) => e.target.select()}
              rows={9}
              className="mt-2 w-full resize-none border border-slate-300 bg-slate-50 p-3 text-xs leading-relaxed text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              data-testid="social-share-copy-post-btn"
              onClick={copyPost}
              variant="outline"
              className="rounded-sm border-slate-300"
            >
              {copied ? (
                <>
                  <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  Copy post
                </>
              )}
            </Button>
            <Button
              size="sm"
              data-testid="social-share-download-img-btn"
              onClick={downloadImage}
              variant="outline"
              className="rounded-sm border-slate-300"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Download image
            </Button>
            <a
              href={linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-share-linkedin-btn"
              className="inline-flex items-center gap-1.5 rounded-sm bg-[#0a66c2] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#085096]"
            >
              <Linkedin className="h-3.5 w-3.5" />
              Open LinkedIn
            </a>
          </div>
          <p className="text-[11px] text-slate-500">
            Tip: upload the PNG when you post — LinkedIn auto-cropping looks
            better than the OG preview.
          </p>
        </div>
      </div>
    </section>
  );
}
